import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import { Chip, CircularProgress, List, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import { UploadMethod } from './types';

// TODO: We need to test drag and drop in the browser for complex file names, folders, and large files. If it works, we can remove this
const IS_DRAG_AND_DROP_TESTED = false;

export const UploadMethodTransferIcon = ({ from, to }: { from: React.ReactNode; to: React.ReactNode }) => (
  <Stack
    direction="row"
    spacing={0.5}
    alignItems="center"
    aria-hidden="true"
    sx={{ width: 70, height: 24, flexShrink: 0 }}
  >
    {from}
    <EastOutlinedIcon color="action" sx={{ fontSize: 14 }} />
    {to}
  </Stack>
);

const UploadMethodOption = ({
  icon,
  title,
  description,
  recommended = false,
  testId,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  recommended?: boolean;
  testId: string;
  onClick: () => void;
}) => (
  <ListItemButton
    data-testid={testId}
    onClick={onClick}
    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
  >
    <ListItemIcon sx={{ minWidth: 72 }}>{icon}</ListItemIcon>
    <ListItemText
      primary={
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{title}</span>
          {recommended ? <Chip color="primary" label="Recommended" size="small" /> : null}
        </Stack>
      }
      secondary={description}
    />
    <CloudUploadOutlinedIcon color="action" />
  </ListItemButton>
);

export const UploadMethodChooser = ({
  canCreateCustomerBucketGrants,
  hasExternalSources,
  isStudyLoading,
  onSelect,
}: {
  canCreateCustomerBucketGrants: boolean;
  hasExternalSources: boolean;
  isStudyLoading: boolean;
  onSelect: (method: UploadMethod) => void;
}) => (
  <List disablePadding aria-label="upload methods">
    {canCreateCustomerBucketGrants ? (
      <UploadMethodOption
        icon={<UploadMethodTransferIcon from={<CloudOutlinedIcon />} to={<CloudOutlinedIcon />} />}
        title="From your organization's Amazon S3 bucket"
        description="Transfer directly using an approved AWS identity. Your AWS administrator may need to configure access."
        recommended
        testId="use-customer-cli"
        onClick={() => onSelect('customer-cli')}
      />
    ) : null}
    <UploadMethodOption
      icon={<UploadMethodTransferIcon from={<LaptopMacOutlinedIcon />} to={<CloudOutlinedIcon />} />}
      title="From this computer or attached storage"
      description="Use temporary upload-only credentials and AWS CLI."
      testId="use-nucleai-storage"
      onClick={() => onSelect('temporary-credentials')}
    />
    {isStudyLoading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
        <CircularProgress size={18} />
        <ListItemText primary="Checking configured study buckets..." />
      </Box>
    ) : hasExternalSources ? (
      <UploadMethodOption
        icon={<UploadMethodTransferIcon from={<FolderOutlinedIcon />} to={<CloudOutlinedIcon />} />}
        title="Browse a configured study bucket"
        description="Select remote files and queue a server-side transfer."
        testId="use-existing-storage"
        onClick={() => onSelect('external-storage')}
      />
    ) : null}
    {IS_DRAG_AND_DROP_TESTED && (
      <UploadMethodOption
        icon={<UploadMethodTransferIcon from={<ComputerOutlinedIcon />} to={<UploadOutlinedIcon />} />}
        title="Drag and drop your slides or browse"
        description="Select files from this computer and upload them in the browser."
        testId="use-local-files"
        onClick={() => onSelect('local-files')}
      />
    )}
  </List>
);
